/**
 * Storage keys used across the app. Consolidated so we never typo a key.
 */
export const StorageKeys = {
  apiUrl: 'dtms.apiUrl',
  accessToken: 'dtms.accessToken',
  refreshToken: 'dtms.refreshToken',
  tokenExpiresAt: 'dtms.tokenExpiresAt',
  driverNumber: 'dtms.driverNumber',
  lastSync: 'dtms.lastSync',
  installPromptDismissed: 'dtms.installPromptDismissed',
  // Remembers which optional-update version the driver dismissed, so we don't
  // nag on every check (forced updates ignore this).
  updateDismissedVersion: 'dtms.updateDismissedVersion',
  // When the daily automatic quarantine retry last ran on this device.
  quarantineRetryAt: 'dtms.quarantineRetryAt',
  // Diagnostics reported in the heartbeat: when an upload last succeeded and
  // the most recent push failure (JSON: { message, at }).
  lastUploadAt: 'dtms.lastUploadAt',
  lastPushError: 'dtms.lastPushError',
  // When this device last re-asserted its push subscription with the server.
  pushReregisterAt: 'dtms.pushReregisterAt',
  // Server-advertised per-tenant feature toggles (JSON), cached for offline boot.
  features: 'dtms.features',
} as const;

/** Stamp a successful queue push (any log batch entry or media upload). */
export function stampUploadSuccess(): void {
  try {
    localStorage.setItem(StorageKeys.lastUploadAt, new Date().toISOString());
  } catch {
    /* storage unavailable */
  }
}

/** Stamp the most recent push failure for heartbeat diagnostics. */
export function stampUploadFailure(message: string): void {
  try {
    localStorage.setItem(
      StorageKeys.lastPushError,
      JSON.stringify({ message: message.slice(0, 200), at: new Date().toISOString() }),
    );
  } catch {
    /* storage unavailable */
  }
}

/**
 * Automatic recovery of quarantined queue items. A server outage burns each
 * entry through its 5 push attempts; without this the backlog sits until a
 * driver manually taps "Reset failed". Once per interval the app re-queues
 * everything recoverable so the fleet self-heals after the outage ends.
 */
export const QuarantineRetryConfig = {
  intervalMs: 24 * 60 * 60 * 1000,
} as const;

/**
 * How often the app re-checks the server's advertised client version.
 */
export const VersionCheckConfig = {
  pollMs: 15 * 60_000,
} as const;

/**
 * Static app identity shown on the login screen ("Pro {version}").
 */
export const AppInfo = {
  version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0',
} as const;

/**
 * Sync intervals (ms). Mirrors the native adaptive sync strategy.
 */
export const SyncIntervals = {
  loadUnload: 2 * 60_000,
  mainMenu: 5 * 60_000,
  background: 10 * 60_000,
  emptyPollBackoffMax: 20 * 60_000,
} as const;

/**
 * GPS configuration.
 */
export const GpsConfig = {
  intervalMs: 5 * 60_000,
  // How long a single currentFix() awaits the persistent watch before giving up
  // (the watch keeps running regardless). Generous so the driver has time to
  // answer the iOS permission prompt.
  highAccuracyTimeoutMs: 20_000,
  staleAfterMs: 6 * 60_000,
  // Minimum spacing between `gps break` ticks. The interval timer (flushed
  // late by the browser after background throttling) and the tab-resume
  // handler can otherwise fire in the same second and write duplicate rows.
  minTickGapMs: 60_000,
} as const;

/**
 * Auth-related thresholds.
 *
 * tokenRefreshBufferMs: refresh the access token this far ahead of its expiry.
 * Generous (10 min) so a backgrounded/suspended tab that resumes near expiry
 * proactively refreshes before firing real requests, instead of eating a 401.
 */
export const AuthConfig = {
  tokenRefreshBufferMs: 10 * 60_000,
} as const;

/**
 * Temperature probe threshold (Celsius). Per Martin: refuse if above -5C.
 */
export const TemperatureConfig = {
  thresholdCelsius: -5,
} as const;
